import ColorThief from "colorthief";
import primaryThemeColors from "~/utils/theme-colors";

interface IHslColor {
    hue: number;
    saturation: number;
    lightness: number;
}

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 * Modified version from: https://stackoverflow.com/a/9493060
 */
const rgbToHsl = ([r, g, b]: number[]): IHslColor => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = (max + min) * 0.5;
    let s = (max + min) * 0.5;
    const l = (max + min) * 0.5;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    return { hue: h, saturation: s, lightness: l };
};

// Returns index of number in array closest to target
const findIndexOfClosest = (nums: number[], target: number) => {
    let closest = Number.MAX_SAFE_INTEGER;
    return nums.reduce((indexOfClosest, number, index) => {
        const dist = Math.abs(target - number);
        if (dist < closest) {
            indexOfClosest = index;
            closest = dist;
        }
        return indexOfClosest;
    }, 0);
};

const findMatchingTheme = (hue: number) => {
    // Make array of the hue of the "top/primary" colors
    const primaryThemeHues = primaryThemeColors.map(color => rgbToHsl(color).hue);

    // Compare hue with theme hues and return index of closest match
    return findIndexOfClosest(primaryThemeHues, hue);
};

/**
 * Adds a theme class to the body element matched to the
 * dominant color of the input image.
 * The comparison is solely based on hue.
 * Uses color-thief for getting the dominant color, see
 * https://github.com/lokesh/color-thief for docs.
 * @param img src of image or the HTML element itself
 */
const setThemeFromImg = (img: HTMLImageElement) => {
    const colorThief = new ColorThief();
    const GRAYSCALE_BOUNDARY = 0.15;
    const GRAYSCALE_THEME_NAME = "theme-10";

    const colorThiefResult = colorThief.getPalette(img, 2);
    if (!colorThiefResult) {
        // Silently fail getting a theme.
        return;
    }
    const dominantColor = colorThiefResult[0];
    const { hue, saturation } = rgbToHsl(dominantColor);
    const matchingThemeIndex = findMatchingTheme(hue);

    const themeContainer = img.closest("body");
    const themeTarget = themeContainer || document.body;

    const currentTheme = themeTarget.className.match(/theme-\d+/);
    if (currentTheme) {
        themeTarget.classList.remove(currentTheme[0]);
    }

    if (saturation < GRAYSCALE_BOUNDARY) {
        themeTarget.classList.add(GRAYSCALE_THEME_NAME);
        return;
    }

    themeTarget.classList.add(`theme-${matchingThemeIndex + 1}`);
};

export default setThemeFromImg;
